import { useEffect, useState } from 'react';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import {
  UserMinus, PlayCircle, CheckCircle2, Circle, X, Send, ClipboardCheck,
  Hourglass, ShieldOff, Bell, AlertTriangle, Lock, ShieldCheck, ShieldAlert, ThumbsUp, ThumbsDown,
} from 'lucide-react';

const KINDS = [
  { v: 'resignation', label: 'Resignation' },
  { v: 'termination', label: 'Termination' },
  { v: 'contract_end', label: 'Contract end' },
  { v: 'retirement', label: 'Retirement' },
];

const MANDATORY = ['knowledge_transfer', 'asset_return', 'final_settlement'];

function StatusBadge({ status }) {
  const map = {
    pending: { c: 'bg-amber-100 text-amber-700', label: 'Pending HR approval' },
    active: { c: 'bg-blue-100 text-blue-700', label: 'In progress' },
    completed: { c: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
    cancelled: { c: 'bg-slate-100 text-slate-500', label: 'Cancelled' },
  };
  const m = map[status] || map.active;
  return <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${m.c}`}>{m.label}</span>;
}

export default function OffboardingSection({ employeeId, employeeName, isSelf, canManage, onChanged }) {
  const { user: currentUser } = useAuth();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showInitiate, setShowInitiate] = useState(false);
  const [showResign, setShowResign] = useState(false);

  const [initiateForm, setInitiateForm] = useState({
    kind: 'resignation', reason: '', last_working_day: '',
    notice_period_days: '', notes: '',
  });
  const [resignForm, setResignForm] = useState({
    last_working_day: '', notice_period_days: 30, reason: '', notes: '',
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${employeeId}/offboarding`);
      setDoc(data || null);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (employeeId) load(); }, [employeeId]); // eslint-disable-line

  const initiate = async () => {
    if (!initiateForm.reason.trim()) return toast.error('Reason is required');
    if (!initiateForm.last_working_day) return toast.error('Last working day is required');
    setBusy(true);
    try {
      const payload = { ...initiateForm };
      if (payload.notice_period_days === '') delete payload.notice_period_days;
      else payload.notice_period_days = +payload.notice_period_days;
      await api.post(`/employees/${employeeId}/offboarding/initiate`, payload);
      toast.success('Offboarding initiated');
      setShowInitiate(false);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const submitResignation = async () => {
    if (!resignForm.last_working_day) return toast.error('Last working day is required');
    setBusy(true);
    try {
      await api.post('/offboarding/resignation', {
        ...resignForm,
        notice_period_days: resignForm.notice_period_days ? +resignForm.notice_period_days : null,
      });
      toast.success('Resignation submitted to HR');
      setShowResign(false);
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const approveResignation = async () => {
    setBusy(true);
    try {
      await api.post(`/offboarding/${doc.id}/approve-resignation`);
      toast.success('Resignation approved');
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setDone = async (key, done) => {
    setBusy(true);
    try {
      await api.patch(`/offboarding/${doc.id}/checklist`, { key, done });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const requestEmployee = async (key) => {
    setBusy(true);
    try {
      await api.post(`/offboarding/${doc.id}/request-task`, { key });
      toast.success('Employee notified');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const verifyTask = async (key, accept) => {
    let note = null;
    if (!accept) {
      note = window.prompt('Reason for rejecting this verification (optional):') || '';
    }
    setBusy(true);
    try {
      await api.post(`/offboarding/${doc.id}/verify-task`, { key, accept, note });
      toast.success(accept ? 'Verified ✓' : 'Sent back for redo');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const completeOffb = async () => {
    if (!window.confirm('Completing offboarding will TERMINATE the employee, revoke their work email login and issue Relieving + Experience letters. Continue?')) return;
    setBusy(true);
    try {
      await api.post(`/offboarding/${doc.id}/complete`);
      toast.success('Offboarding completed · letters issued');
      load(); onChanged?.();
    } catch (e) {
      // Show the FULL detail from backend (e.g., the list of pending mandatory items)
      const detail = e.response?.data?.detail;
      toast.error(formatApiError(detail) || e.message || 'Failed to complete', { duration: 7000 });
    }
    finally { setBusy(false); }
  };

  const cancelOffb = async () => {
    if (!window.confirm('Cancel this offboarding? The employee will remain active.')) return;
    setBusy(true);
    try {
      await api.post(`/offboarding/${doc.id}/cancel`);
      toast.success('Offboarding cancelled');
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // ---------- render ----------
  if (loading) return <div className="bg-white border border-slate-200 rounded-2xl p-6 text-slate-400 text-sm">Loading…</div>;

  // No offboarding doc
  if (!doc) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="offboarding-section">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0">
            <UserMinus className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Offboarding</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">No offboarding in progress</h2>
            <p className="text-xs text-slate-500 mt-1">{isSelf ? 'Planning to leave? Submit your resignation to HR — they\'ll guide you through the rest.' : 'Initiate offboarding to start the checklist-based exit workflow.'}</p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isSelf && (
            <Dialog open={showResign} onOpenChange={setShowResign}>
              <DialogTrigger asChild>
                <Button data-testid="submit-resignation-btn" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                  <Send className="w-4 h-4 mr-2" /> Submit resignation
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md" data-testid="resignation-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Submit resignation</DialogTitle>
                  <DialogDescription>HR will review your request and confirm the last working day. You&apos;ll be notified by email.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2 text-sm">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Proposed last working day *</Label>
                    <Input data-testid="resign-lwd" type="date" value={resignForm.last_working_day}
                           onChange={(e) => setResignForm({ ...resignForm, last_working_day: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Notice period (days)</Label>
                    <Input data-testid="resign-notice" type="number" value={resignForm.notice_period_days}
                           onChange={(e) => setResignForm({ ...resignForm, notice_period_days: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Reason</Label>
                    <Textarea data-testid="resign-reason" rows={2} value={resignForm.reason}
                              onChange={(e) => setResignForm({ ...resignForm, reason: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Notes (optional)</Label>
                    <Textarea data-testid="resign-notes" rows={2} value={resignForm.notes}
                              onChange={(e) => setResignForm({ ...resignForm, notes: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowResign(false)} className="rounded-full">Cancel</Button>
                  <Button onClick={submitResignation} disabled={busy} data-testid="submit-resignation-confirm" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                    {busy ? 'Submitting…' : 'Submit'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canManage && (
            <Dialog open={showInitiate} onOpenChange={setShowInitiate}>
              <DialogTrigger asChild>
                <Button data-testid="initiate-offboarding-btn" variant={isSelf ? 'outline' : 'default'}
                        className={`rounded-full ${isSelf ? '' : 'bg-[#3D4A8E] hover:bg-[#5C6BC0]'}`}>
                  <PlayCircle className="w-4 h-4 mr-2" /> Initiate offboarding
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md" data-testid="initiate-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Initiate offboarding</DialogTitle>
                  <DialogDescription>This starts the checklist-based exit workflow and notifies the employee.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 mt-2 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Type *</Label>
                      <Select value={initiateForm.kind} onValueChange={(v) => setInitiateForm({ ...initiateForm, kind: v })}>
                        <SelectTrigger data-testid="initiate-kind" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600">Notice (days)</Label>
                      <Input data-testid="initiate-notice" type="number" value={initiateForm.notice_period_days}
                             onChange={(e) => setInitiateForm({ ...initiateForm, notice_period_days: e.target.value })} className="rounded-xl mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Last working day *</Label>
                    <Input data-testid="initiate-lwd" type="date" value={initiateForm.last_working_day}
                           onChange={(e) => setInitiateForm({ ...initiateForm, last_working_day: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Reason *</Label>
                    <Textarea data-testid="initiate-reason" rows={2} value={initiateForm.reason}
                              onChange={(e) => setInitiateForm({ ...initiateForm, reason: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs font-semibold text-slate-600">Notes (optional)</Label>
                    <Textarea data-testid="initiate-notes" rows={2} value={initiateForm.notes}
                              onChange={(e) => setInitiateForm({ ...initiateForm, notes: e.target.value })} className="rounded-xl mt-1" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowInitiate(false)} className="rounded-full">Cancel</Button>
                  <Button onClick={initiate} disabled={busy} data-testid="initiate-confirm" className="rounded-full bg-[#3D4A8E] hover:bg-[#5C6BC0]">
                    {busy ? 'Initiating…' : 'Start offboarding'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
    );
  }

  // Has an offboarding — render its detail
  const cl = doc.checklist || {};
  const progress = doc.progress?.pct ?? 0;
  const missingMandatory = MANDATORY.filter(k => !cl[k]?.done);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-5" data-testid="offboarding-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${doc.status === 'completed' ? 'bg-emerald-50 text-emerald-600' : doc.status === 'pending' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-[#3D4A8E]'}`}>
            {doc.status === 'completed' ? <ClipboardCheck className="w-5 h-5" /> : doc.status === 'pending' ? <Hourglass className="w-5 h-5" /> : <PlayCircle className="w-5 h-5" />}
          </div>
          <div className="min-w-0">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Offboarding · {doc.kind?.replace('_', ' ')}</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900 mt-1">{employeeName || doc.employee_name}</h2>
            <div className="mt-1 flex items-center gap-3 flex-wrap text-xs text-slate-500">
              <StatusBadge status={doc.status} />
              {doc.last_working_day && <span>Last working day: <b className="text-slate-700">{doc.last_working_day}</b></span>}
              {doc.notice_period_days && <span>Notice: <b className="text-slate-700">{doc.notice_period_days} days</b></span>}
            </div>
          </div>
        </div>
        {doc.status === 'active' && canManage && (
          <Button variant="ghost" size="sm" onClick={cancelOffb} disabled={busy} data-testid="cancel-offboarding"
                  className="text-red-600 hover:bg-red-50">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        )}
      </div>

      {doc.reason && (
        <div className="bg-slate-50 border-l-2 border-[#3D4A8E] px-3 py-2 text-sm text-slate-700">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-0.5">Reason</div>
          {doc.reason}
        </div>
      )}

      {doc.status === 'pending' && canManage && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm"><b>Resignation submitted</b> — review and approve to start the offboarding checklist.</div>
          <Button onClick={approveResignation} disabled={busy} data-testid="approve-resignation" className="rounded-full bg-amber-600 hover:bg-amber-700">
            <CheckCircle2 className="w-4 h-4 mr-2" /> Approve resignation
          </Button>
        </div>
      )}
      {doc.status === 'pending' && isSelf && !canManage && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
          ⏳ Your resignation has been submitted. HR will review and approve shortly. You&apos;ll receive an email confirmation.
        </div>
      )}

      {/* Progress + Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Checklist</div>
          <div className="text-xs text-slate-500">{doc.progress?.done}/{doc.progress?.total} done · <b className="text-slate-700">{progress}%</b></div>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
          <div className="h-full bg-[#3D4A8E] transition-all" style={{ width: `${progress}%` }} />
        </div>
        <div className="space-y-2">
          {Object.entries(cl).map(([key, item]) => {
            const mandatory = MANDATORY.includes(key);
            const empOwned = item.assignee === 'employee';
            const canEmpAct = isSelf && empOwned && doc.status === 'active';
            const canHrAct = canManage && doc.status === 'active';
            const requested = !!item.requested_at;
            const pendingVerify = !!item.pending_hr_verify;
            const markerName = item.marked_by || item.employee_marked_by;
            const markerId = item.marked_by_id;
            const markedRole = item.marked_by_role || (item.employee_marked_by ? 'employee' : null);
            // The verifier must be a different person than the marker (true 2-step).
            const canVerify = canHrAct && pendingVerify && (!markerId || markerId !== currentUser?.id);
            return (
              <div key={key} data-testid={`checklist-${key}`}
                   className={`flex items-start gap-3 rounded-xl border px-3 py-3 ${item.done ? 'border-emerald-200 bg-emerald-50/40' : (pendingVerify ? 'border-amber-300 bg-amber-50/60' : (requested ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 bg-white'))}`}>
                {item.done
                  ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  : pendingVerify
                    ? <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    : <Circle className="w-5 h-5 text-slate-300 flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm flex items-center gap-2 flex-wrap ${item.done ? 'text-slate-500' : 'text-slate-800 font-medium'}`}>
                    <span>{item.label}</span>
                    {mandatory && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-700">Required</span>}
                    <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${empOwned ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-600'}`}>
                      {empOwned ? 'Employee' : 'HR'}
                    </span>
                    {pendingVerify && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-1" data-testid={`pending-verify-${key}`}>
                        <ShieldAlert className="w-3 h-3" /> Pending HR verification
                      </span>
                    )}
                    {requested && !item.done && !pendingVerify && (
                      <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                        <Bell className="w-3 h-3" /> Action requested
                      </span>
                    )}
                  </div>
                  {item.done && item.completed_by && (
                    <div className="text-[11px] text-slate-500 mt-1">Done by <b>{item.completed_by}</b>{item.completed_at ? ` · ${item.completed_at.slice(0, 10)}` : ''}</div>
                  )}
                  {pendingVerify && markerName && (
                    <div className="text-[11px] text-amber-800 mt-1">
                      Marked by <b>{markerName}</b>{markedRole ? ` (${markedRole})` : ''}{item.marked_at ? ` · ${item.marked_at.slice(0, 10)}` : ''}
                      {' '}— awaiting verification by a different HR/Admin.
                    </div>
                  )}
                  {requested && !item.done && !pendingVerify && (
                    <div className="text-[11px] text-amber-700 mt-1">Requested by <b>{item.requested_by}</b>{item.requested_at ? ` · ${item.requested_at.slice(0, 10)}` : ''}</div>
                  )}
                  {item.rejection_note && !item.done && !pendingVerify && (
                    <div className="text-[11px] text-red-600 mt-1">Previously rejected: {item.rejection_note}</div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  {/* 2-step verify controls (HR side) */}
                  {canHrAct && pendingVerify && (
                    <>
                      <Button size="sm" onClick={() => verifyTask(key, true)} disabled={busy || !canVerify}
                              data-testid={`verify-${key}`}
                              title={canVerify ? '' : 'The HR who marked this item cannot verify their own action'}
                              className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8 disabled:opacity-40">
                        <ThumbsUp className="w-3.5 h-3.5 mr-1.5" /> Verify
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => verifyTask(key, false)} disabled={busy}
                              data-testid={`reject-verify-${key}`}
                              className="rounded-full h-8 text-red-600 border-red-300 hover:bg-red-50">
                        <ThumbsDown className="w-3.5 h-3.5 mr-1.5" /> Reject
                      </Button>
                    </>
                  )}
                  {canEmpAct && !item.done && !pendingVerify && (
                    <Button size="sm" onClick={() => setDone(key, true)} disabled={busy}
                            data-testid={`emp-done-${key}`} className="rounded-full bg-emerald-600 hover:bg-emerald-700 h-8">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark done
                    </Button>
                  )}
                  {canHrAct && empOwned && !item.done && !pendingVerify && !requested && (
                    <Button size="sm" variant="outline" onClick={() => requestEmployee(key)} disabled={busy}
                            data-testid={`req-${key}`} className="rounded-full h-8 text-amber-700 border-amber-300 hover:bg-amber-50">
                      <Bell className="w-3.5 h-3.5 mr-1.5" /> Request employee
                    </Button>
                  )}
                  {canHrAct && !item.done && !pendingVerify && (
                    <Button size="sm" variant="outline" onClick={() => setDone(key, true)} disabled={busy}
                            data-testid={`hr-done-${key}`} className="rounded-full h-8">
                      <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> {empOwned ? 'Mark on behalf' : 'Mark done'}
                    </Button>
                  )}
                  {canHrAct && item.done && (
                    <Button size="sm" variant="ghost" onClick={() => setDone(key, false)} disabled={busy}
                            data-testid={`hr-undo-${key}`} className="rounded-full h-8 text-slate-500 hover:text-red-600">
                      Undo
                    </Button>
                  )}
                  {isSelf && pendingVerify && (
                    <span className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid={`emp-pending-${key}`}>
                      <Hourglass className="w-3 h-3" /> HR will verify
                    </span>
                  )}
                  {isSelf && !empOwned && !item.done && !pendingVerify && (
                    <span className="text-[11px] text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> HR will handle</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Complete CTA */}
      {doc.status === 'active' && canManage && (
        <div className="border-t border-slate-100 pt-4 space-y-3">
          {missingMandatory.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800 flex items-start gap-2" data-testid="missing-mandatory">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>
                <b>Cannot complete yet.</b> Pending required items:
                <ul className="list-disc list-inside mt-1 text-red-700">
                  {missingMandatory.map(k => <li key={k}>{cl[k]?.label || k}</li>)}
                </ul>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-xs text-slate-500 max-w-md">
              Completing will <b>terminate the employee</b>, <b>revoke their work-email login</b> and issue Relieving + Experience letters.
            </div>
            <Button onClick={completeOffb} disabled={busy || missingMandatory.length > 0} data-testid="complete-offboarding"
                    className="rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
              <ShieldOff className="w-4 h-4 mr-2" /> Complete & issue letters
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
